<app-page-header title="Fare Bundles" subtitle="Manage package content and provider ancillary mappings">
    <div actions>
        @if (activeTab === 'bundle-content') {
            <p-button label="Add New" (click)="openAdd()"></p-button>
        }
    </div>
</app-page-header>

<div class="table-card">
    <p-confirmpopup />
    <p-dialog [(visible)]="displayDialog" [modal]="true" [header]="dialogHeader" [closable]="true" [style]="{ width: '820px', maxWidth: '95vw' }">
        <app-ancillary-create-edit
            [mode]="dialogMode"
            [data]="dialogData"
            (cancel)="onDialogCancel()"
            (save)="onDialogSave($event)"
        ></app-ancillary-create-edit>
    </p-dialog>

    <p-tabs [(value)]="activeTab">
        <p-tablist>
            <p-tab value="bundle-content">Bundle Content</p-tab>
            <p-tab value="alternative-message">Alternative Package Message</p-tab>
        </p-tablist>
        <p-tabpanels>
            <p-tabpanel value="bundle-content">
                <div class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-7 gap-2 my-4">
                    <p-select [options]="providerOptions" [(ngModel)]="filters.providerCode" (onChange)="applyFilters()" placeholder="Provider" [showClear]="true" appendTo="body" />
                    <p-select [options]="airlineOptions" [(ngModel)]="filters.airlineCode" (onChange)="applyFilters()" placeholder="Airline" [showClear]="true" appendTo="body" />
                    <p-select [options]="fareOptions" [(ngModel)]="filters.fareTypeName" (onChange)="applyFilters()" placeholder="Fare Package" [showClear]="true" appendTo="body" />
                    <p-select [options]="categoryOptions" [(ngModel)]="filters.categoryName" (onChange)="applyFilters()" placeholder="Category" [showClear]="true" appendTo="body" />
                    <p-select [options]="typeOptions" [(ngModel)]="filters.mode" (onChange)="applyFilters()" placeholder="Type" appendTo="body" />
                    <p-select [options]="statusOptions" [(ngModel)]="filters.status" (onChange)="applyFilters()" placeholder="Status" [showClear]="true" appendTo="body" />
                    <div class="flex gap-2">
                        <input pInputText [(ngModel)]="filters.text" (input)="applyFilters()" placeholder="Search" class="w-full" />
                        <p-button icon="pi pi-filter-slash" [text]="true" severity="secondary" (click)="clearFilters()"></p-button>
                    </div>
                </div>

                <p-table
                    [value]="filteredAncillaries"
                    [loading]="loading"
                    [paginator]="true"
                    [rows]="10"
                    [scrollable]="true"
                    scrollHeight="flex"
                    dataKey="id"
                    [rowHover]="true"
                >
                    <ng-template #header>
                        <tr>
                            <th pSortableColumn="providerCode">Provider <p-sortIcon field="providerCode"></p-sortIcon></th>
                            <th pSortableColumn="airlineCode">Airline <p-sortIcon field="airlineCode"></p-sortIcon></th>
                            <th pSortableColumn="fareTypeName">Fare Package <p-sortIcon field="fareTypeName"></p-sortIcon></th>
                            <th pSortableColumn="categoryName">Category <p-sortIcon field="categoryName"></p-sortIcon></th>
                            <th>Display Text</th>
                            <th pSortableColumn="amount" class="w-[90px]">Amount <p-sortIcon field="amount"></p-sortIcon></th>
                            <th pSortableColumn="unit" class="w-[80px]">Unit <p-sortIcon field="unit"></p-sortIcon></th>
                            <th pSortableColumn="mode" class="w-[150px]">Type <p-sortIcon field="mode"></p-sortIcon></th>
                            <th pSortableColumn="status" class="w-[120px]">Status <p-sortIcon field="status"></p-sortIcon></th>
                            <th class="w-[110px]"></th>
                        </tr>
                    </ng-template>
                    <ng-template #body let-row>
                        <tr>
                            <td>{{ row.providerCode }}</td>
                            <td>{{ row.airlineCode || 'All' }}</td>
                            <td>{{ row.fareTypeName || 'All packages' }}</td>
                            <td>{{ row.categoryName }}</td>
                            <td>
                                <div class="text-sm text-slate-700">{{ row.titlesByLanguage?.EN || row.uiDisplayText }}</div>
                                <div class="text-xs text-slate-500" *ngIf="row.mode !== 'BUNDLE_CONTENT'">Key: {{ row.ancillaryKey }}</div>
                            </td>
                            <td>{{ row.amount ?? '-' }}</td>
                            <td>{{ row.unit || '-' }}</td>
                            <td>{{ displayType(row) }}</td>
                            <td>
                                <p-select
                                    [options]="statusOptions"
                                    [(ngModel)]="row.status"
                                    (onChange)="onStatusChange($event, row)"
                                    [appendTo]="'body'"
                                    optionLabel="label"
                                    optionValue="value"
                                    [style]="{ width: '120px' }"
                                />
                            </td>
                            <td class="text-right">
                                <div class="flex gap-2 justify-end">
                                    <p-button [text]="true" severity="secondary" icon="pi pi-pencil" (click)="onEdit(row)"></p-button>
                                    <p-button [text]="true" severity="danger" icon="pi pi-trash" (click)="confirmDelete($event, row)"></p-button>
                                </div>
                            </td>
                        </tr>
                    </ng-template>
                </p-table>
            </p-tabpanel>
            <p-tabpanel value="alternative-message">
                <div class="max-w-5xl py-4">
                    <p-message
                        severity="info"
                        text="Manage the title and description shown on alternative package cards for each provider. Empty language fields use the default system text."
                        styleClass="mb-4"
                    ></p-message>

                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                        <div>
                            <label for="alternative-provider" class="block mb-2">Provider</label>
                            <p-select
                                inputId="alternative-provider"
                                [options]="allProviderOptions"
                                [(ngModel)]="selectedMessageProviderCode"
                                (onChange)="onMessageProviderChange()"
                                placeholder="Select provider"
                                [filter]="true"
                                appendTo="body"
                                class="w-full"
                            />
                        </div>
                    </div>

                    @if (selectedMessageProviderCode) {
                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            @for (language of ['EN', 'TR', 'DE', 'NL']; track language) {
                                <section class="border border-slate-200 rounded-md p-3">
                                    <div class="font-medium mb-3">{{ language }}</div>
                                    <label class="block mb-2" [for]="'alternative-title-' + language">Title</label>
                                    <input
                                        pInputText
                                        [id]="'alternative-title-' + language"
                                        [(ngModel)]="messageTranslations.titlesByLanguage[language]"
                                        [placeholder]="getFallbackTitle(language)"
                                        class="w-full mb-1"
                                    />
                                    <div class="text-xs text-slate-500 mb-3">Fallback: {{ getFallbackTitle(language) || '-' }}</div>

                                    <label class="block mb-2" [for]="'alternative-description-' + language">Description</label>
                                    <textarea
                                        pInputTextarea
                                        [id]="'alternative-description-' + language"
                                        [(ngModel)]="messageTranslations.descriptionsByLanguage[language]"
                                        [placeholder]="getFallbackDescription(language)"
                                        rows="4"
                                        class="w-full"
                                    ></textarea>
                                    <div class="text-xs text-slate-500 mt-1">Fallback: {{ getFallbackDescription(language) || '-' }}</div>
                                </section>
                            }
                        </div>
                        <div class="flex justify-end mt-4">
                            <p-button label="Save" icon="pi pi-save" [loading]="messageSaving" [disabled]="messageLoading" (click)="saveAlternativePackageMessage()"></p-button>
                        </div>
                    }
                </div>
            </p-tabpanel>
        </p-tabpanels>
    </p-tabs>
</div>
